import { useState } from 'react'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Progress } from '@/components/ui/progress'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  Calendar,
  ChevronDown,
  Download,
  RefreshCcw,
  DollarSign,
  TrendingUp,
  TrendingDown,
  Users,
  Percent,
  BarChart3,
  PieChart,
  Trophy,
  Target,
  Flame,
} from 'lucide-react'
import { cn } from '@/lib/utils'

type DateRange = 'today' | 'week' | 'month' | 'quarter'

export function GameReportPage() {
  const [dateRange, setDateRange] = useState<DateRange>('today')

  const dateRangeLabels: Record<DateRange, string> = {
    today: 'วันนี้',
    week: 'สัปดาห์นี้',
    month: 'เดือนนี้',
    quarter: 'ไตรมาสนี้',
  }

  // Mock data
  const summaryStats = {
    totalBets: 285000000,
    totalWin: 268500000,
    netRevenue: 16500000,
    totalRounds: 1856420,
    uniquePlayers: 35800,
    avgBetSize: 153,
    rtp: 94.2,
    houseEdge: 5.8,
  }

  const categoryPerformance = [
    { name: 'สล็อต', icon: '🎰', bets: 125000000, win: 118500000, revenue: 6500000, rounds: 850000, players: 28500, rtp: 94.8 },
    { name: 'คาสิโนสด', icon: '🃏', bets: 95000000, win: 89300000, revenue: 5700000, rounds: 320000, players: 15200, rtp: 94.0 },
    { name: 'กีฬา', icon: '⚽', bets: 45000000, win: 42500000, revenue: 2500000, rounds: 125000, players: 12500, rtp: 94.4 },
    { name: 'ยิงปลา', icon: '🐟', bets: 12000000, win: 11200000, revenue: 800000, rounds: 420000, players: 8200, rtp: 93.3 },
    { name: 'หวย', icon: '🎱', bets: 8000000, win: 7000000, revenue: 1000000, rounds: 141420, players: 5600, rtp: 87.5 },
  ]

  const topGames = [
    { rank: 1, name: 'Mahjong Ways 2', provider: 'PG Soft', bets: 18200000, win: 17100000, revenue: 1100000, rtp: 93.9, growth: 15.2 },
    { rank: 2, name: 'Gates of Olympus', provider: 'Pragmatic', bets: 15850000, win: 14900000, revenue: 950000, rtp: 94.0, growth: 8.5 },
    { rank: 3, name: 'Baccarat', provider: 'SA Gaming', bets: 85000000, win: 80500000, revenue: 4500000, rtp: 94.7, growth: 12.3 },
    { rank: 4, name: 'Fortune Tiger', provider: 'PG Soft', bets: 22500000, win: 21200000, revenue: 1300000, rtp: 94.2, growth: 25.8 },
    { rank: 5, name: 'Sweet Bonanza', provider: 'Pragmatic', bets: 12500000, win: 11800000, revenue: 700000, rtp: 94.4, growth: 5.2 },
  ]

  const providerPerformance = [
    { name: 'PG Soft', bets: 85000000, win: 80200000, revenue: 4800000, games: 85, players: 22000 },
    { name: 'Pragmatic Play', bets: 65000000, win: 61500000, revenue: 3500000, games: 120, players: 18500 },
    { name: 'SA Gaming', bets: 95000000, win: 89800000, revenue: 5200000, games: 25, players: 12500 },
    { name: 'Evolution', bets: 25000000, win: 23600000, revenue: 1400000, games: 45, players: 8500 },
    { name: 'JILI', bets: 15000000, win: 13400000, revenue: 1600000, games: 75, players: 9500 },
  ]

  const hourlyData = [
    { hour: '00:00', bets: 8500000, players: 2100 },
    { hour: '04:00', bets: 4200000, players: 1200 },
    { hour: '08:00', bets: 12500000, players: 3500 },
    { hour: '12:00', bets: 18000000, players: 4800 },
    { hour: '16:00', bets: 22000000, players: 5500 },
    { hour: '20:00', bets: 35000000, players: 8200 },
  ]

  const formatNumber = (num: number) => new Intl.NumberFormat('th-TH').format(num)
  const formatCurrency = (amount: number) => {
    if (amount >= 1000000) return `฿${(amount / 1000000).toFixed(1)}M`
    if (amount >= 1000) return `฿${(amount / 1000).toFixed(0)}K`
    return new Intl.NumberFormat('th-TH', {
      style: 'currency',
      currency: 'THB',
      minimumFractionDigits: 0,
    }).format(amount)
  }

  const maxBets = Math.max(...categoryPerformance.map(c => c.bets))

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold">รายงานเกม</h1>
          <p className="text-muted-foreground">สรุปประสิทธิภาพและสถิติเกมทั้งหมด</p>
        </div>
        <div className="flex items-center gap-2">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" className="gap-2">
                <Calendar className="h-4 w-4" />
                {dateRangeLabels[dateRange]}
                <ChevronDown className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent>
              {Object.entries(dateRangeLabels).map(([key, label]) => (
                <DropdownMenuItem key={key} onClick={() => setDateRange(key as DateRange)}>
                  {label}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
          <Button variant="outline" size="icon">
            <RefreshCcw className="h-4 w-4" />
          </Button>
          <Button variant="outline" className="gap-2">
            <Download className="h-4 w-4" />
            ส่งออก
          </Button>
        </div>
      </div>

      {/* Main Stats */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div className="p-2.5 rounded-lg bg-blue-500/10">
                <DollarSign className="h-5 w-5 text-blue-500" />
              </div>
              <Badge variant="secondary" className="bg-win/10 text-win border-0 gap-1">
                <TrendingUp className="h-3 w-3" />
                +12.5%
              </Badge>
            </div>
            <div className="mt-3">
              <p className="text-xs text-muted-foreground">ยอดเดิมพันรวม</p>
              <p className="text-2xl font-bold">{formatCurrency(summaryStats.totalBets)}</p>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div className="p-2.5 rounded-lg bg-win/10">
                <TrendingUp className="h-5 w-5 text-win" />
              </div>
            </div>
            <div className="mt-3">
              <p className="text-xs text-muted-foreground">รายได้สุทธิ</p>
              <p className="text-2xl font-bold text-win">{formatCurrency(summaryStats.netRevenue)}</p>
              <p className="text-xs text-muted-foreground mt-1">House Edge: {summaryStats.houseEdge}%</p>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div className="p-2.5 rounded-lg bg-purple-500/10">
                <Users className="h-5 w-5 text-purple-500" />
              </div>
            </div>
            <div className="mt-3">
              <p className="text-xs text-muted-foreground">ผู้เล่นที่ใช้งาน</p>
              <p className="text-2xl font-bold">{formatNumber(summaryStats.uniquePlayers)}</p>
              <p className="text-xs text-muted-foreground mt-1">{formatNumber(summaryStats.totalRounds)} รอบเล่น</p>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div className="p-2.5 rounded-lg bg-orange-500/10">
                <Percent className="h-5 w-5 text-orange-500" />
              </div>
            </div>
            <div className="mt-3">
              <p className="text-xs text-muted-foreground">RTP เฉลี่ย</p>
              <p className="text-2xl font-bold">{summaryStats.rtp}%</p>
              <p className="text-xs text-muted-foreground mt-1">เฉลี่ย ฿{formatNumber(summaryStats.avgBetSize)}/bet</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Category Performance */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <PieChart className="h-5 w-5" />
            ประสิทธิภาพตามหมวดหมู่
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {categoryPerformance.map((cat, idx) => (
              <div key={cat.name} className="space-y-2">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="text-lg">{cat.icon}</span>
                    <span className="font-medium">{cat.name}</span>
                  </div>
                  <div className="flex items-center gap-6 text-sm">
                    <div className="text-right">
                      <p className="font-medium">{formatCurrency(cat.bets)}</p>
                      <p className="text-xs text-muted-foreground">ยอดเดิมพัน</p>
                    </div>
                    <div className="text-right">
                      <p className="font-semibold text-win">{formatCurrency(cat.revenue)}</p>
                      <p className="text-xs text-muted-foreground">รายได้</p>
                    </div>
                    <div className="text-right w-16">
                      <p className="font-medium">{cat.rtp}%</p>
                      <p className="text-xs text-muted-foreground">RTP</p>
                    </div>
                  </div>
                </div>
                <Progress 
                  value={(cat.bets / maxBets) * 100} 
                  className="h-2"
                  indicatorClassName={cn(
                    idx === 0 && "bg-blue-500",
                    idx === 1 && "bg-purple-500",
                    idx === 2 && "bg-green-500",
                    idx === 3 && "bg-cyan-500",
                    idx === 4 && "bg-orange-500"
                  )}
                />
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Top Games & Provider Performance */}
      <div className="grid gap-6 lg:grid-cols-2">
        {/* Top Games */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Trophy className="h-5 w-5" />
              Top 5 เกมยอดนิยม
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {topGames.map((game) => (
                <div key={game.rank} className="flex items-center gap-4 p-3 rounded-lg bg-muted/30">
                  <div className={cn(
                    "h-10 w-10 rounded-full flex items-center justify-center font-bold text-lg",
                    game.rank === 1 && "bg-yellow-500/20 text-yellow-500",
                    game.rank === 2 && "bg-gray-400/20 text-gray-400",
                    game.rank === 3 && "bg-orange-600/20 text-orange-600",
                    game.rank > 3 && "bg-muted text-muted-foreground"
                  )}>
                    {game.rank}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="font-medium truncate">{game.name}</p>
                      {game.growth > 20 && <Flame className="h-4 w-4 text-destructive" />}
                    </div>
                    <p className="text-xs text-muted-foreground">{game.provider}</p>
                  </div>
                  <div className="text-right">
                    <p className="font-semibold text-win">{formatCurrency(game.revenue)}</p>
                    <div className={cn(
                      "text-xs flex items-center justify-end gap-1",
                      game.growth >= 0 ? "text-win" : "text-destructive"
                    )}>
                      {game.growth >= 0 ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />}
                      {game.growth >= 0 ? '+' : ''}{game.growth}%
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Provider Performance */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <BarChart3 className="h-5 w-5" />
              ประสิทธิภาพผู้ให้บริการ
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {providerPerformance.map((provider, idx) => (
                <div key={provider.name} className="flex items-center justify-between py-2 border-b last:border-0">
                  <div className="flex items-center gap-3">
                    <div className="h-8 w-8 rounded-lg bg-primary/10 flex items-center justify-center font-bold text-sm text-primary">
                      {idx + 1}
                    </div>
                    <div>
                      <p className="font-medium">{provider.name}</p>
                      <p className="text-xs text-muted-foreground">
                        {provider.games} เกม • {formatNumber(provider.players)} ผู้เล่น
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="font-semibold text-win">{formatCurrency(provider.revenue)}</p>
                    <p className="text-xs text-muted-foreground">จาก {formatCurrency(provider.bets)}</p>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Hourly Stats */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Target className="h-5 w-5" />
            ยอดเดิมพันรายชั่วโมง
          </CardTitle>
          <CardDescription>แสดงปริมาณการเดิมพันในแต่ละช่วงเวลา</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-6 gap-4">
            {hourlyData.map((item) => {
              const maxHourlyBets = Math.max(...hourlyData.map(h => h.bets))
              const heightPercent = (item.bets / maxHourlyBets) * 100
              return (
                <div key={item.hour} className="text-center">
                  <div className="h-32 flex flex-col justify-end mb-2">
                    <div 
                      className="bg-primary/60 rounded-t-sm mx-auto w-10"
                      style={{ height: `${heightPercent}%` }}
                    />
                  </div>
                  <p className="text-xs font-medium">{item.hour}</p>
                  <p className="text-xs text-muted-foreground">{formatCurrency(item.bets)}</p>
                  <div className="flex items-center justify-center gap-1 text-xs mt-1">
                    <Users className="h-3 w-3 text-muted-foreground" />
                    <span>{formatNumber(item.players)}</span>
                  </div>
                </div>
              )
            })}
          </div>
        </CardContent>
      </Card>
    </div>
  )
}










